/**
 * Client Component wrapper for EventsThemeBlock
 * Provides EventDrawerProvider context for drawer functionality
 * Uses @uicwebmaster/events package components
 * Uses BlockLayout-style scrollarea for horizontal scrolling
 */

'use client';

import { buttonVariants } from '@/components/ui/Button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { EventTeaser, EventType } from '@uicwebmaster/events';
import { EventCard, EventDrawerProvider, EventsComponentsProvider } from '@uicwebmaster/events';
import { Asterisk } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';

interface EventsThemeBlockClientProps {
  /** Events data from server */
  events: EventTeaser[];
  /** Optional title for the block */
  title?: string;
  /** Layout style */
  layout?: 'default' | 'grid' | 'list' | 'compact' | 'featured' | 'scrollarea';
  /** Base URL for event links */
  baseUrl?: string;
  /** Custom CSS classes */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** URL to redirect to when the "See All" button is clicked */
  seeAllUrl?: string;
  /** Show past events (default: true) */
  showPastEvents?: boolean;
  /** Width of each item in pixels (for scrollarea layout) */
  itemWidth?: number;
}

/**
 * Grid container classes based on layout
 * Maps layout variants to Tailwind grid classes
 */
const gridClassesMap: Record<string, string> = {
  grid: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  list: 'flex flex-col gap-4',
  compact: 'flex flex-col gap-1',
  default: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
  featured: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
  scrollarea: '', // No grid classes for scrollarea layout
};

export function EventsThemeBlockClient({
  events,
  title,
  layout = 'default',
  baseUrl = '/events',
  className = '',
  emptyMessage = 'No events found for the selected theme(s).',
  seeAllUrl,
  showPastEvents = true,
  itemWidth = 280,
}: EventsThemeBlockClientProps) {
  const gridClasses = gridClassesMap[layout] || gridClassesMap.default;

  // Filter events based on showPastEvents (same logic as @uicwebmaster/events)
  const filteredEvents = useMemo(() => {
    if (showPastEvents) {
      return events;
    }

    const now = new Date();
    return events.filter((event) => {
      const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
      if (!endTimestamp) return false;
      const eventEnd = new Date(endTimestamp * 1000);
      return eventEnd >= now;
    });
  }, [events, showPastEvents]);

  if (filteredEvents.length === 0) {
    return (
      <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  // Use scrollarea layout (same as BlockLayout)
  if (layout === 'scrollarea') {
    return (
      <EventsComponentsProvider
        config={{
          LinkComponent: Link,
          ImageComponent: Image,
          baseUrl,
        }}
      >
        <EventDrawerProvider>
          <div className={`space-y-4 p-4 lg:px-6 bg-slate-500/5 rounded ${className}`}>
            {(title || seeAllUrl) && (
              <div className="space-y-2">
                <div className="flex gap-2 justify-between items-center">
                  {title && <h3 className="opacity-80">{title}</h3>}
                  {seeAllUrl && (
                    <Link
                      href={seeAllUrl}
                      className={buttonVariants({ variant: 'ghost' }) + ' text-xs text-muted-foreground ml-auto'}
                    >
                      see all
                      <Asterisk className="size-4" />
                    </Link>
                  )}
                </div>
              </div>
            )}
            <ScrollArea className="w-full" orientation="horizontal">
              <div className="flex gap-6 pb-4" style={{ width: 'max-content' }}>
                {filteredEvents.map((event) => (
                  <div key={event.id} style={{ width: `${itemWidth}px` }}>
                    <EventCard
                      event={event as unknown as EventType}
                      baseUrl={baseUrl}
                      variant="default"
                      showPastIndicator={true}
                      clickable={false} // Non-clickable with Details button, opens drawer
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </EventDrawerProvider>
      </EventsComponentsProvider>
    );
  }

  // Use grid/list/compact layouts (original behavior)
  return (
    <EventsComponentsProvider
      config={{
        LinkComponent: Link,
        ImageComponent: Image,
        baseUrl,
      }}
    >
      <EventDrawerProvider>
        <div className={`${gridClasses} ${className}`}>
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event as unknown as EventType}
              baseUrl={baseUrl}
              variant={layout}
              showPastIndicator={true}
              clickable={false} // Non-clickable with Details button, opens drawer
            />
          ))}
        </div>
      </EventDrawerProvider>
    </EventsComponentsProvider>
  );
}

